import type { LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowUpRight, PhoneCall, ScrollText } from 'lucide-react'

import { PageHeader } from '@/components/layout/PageHeader'

/**
 * /help/avito — интерактивные инструкции по регламенту обработки заявок Avito
 * (C2C-осмотры). Два режима работы команды:
 *  - «Со Стрижом в Москве» — 3 роли (фронт-офис, диспетчер Москва, диспетчер регионы)
 *  - «Без Стрижа» — 2 роли (фронт-офис, диспетчер; все инспекторы по звонку)
 *
 * Плюс «Помощник звонка оператора» (carlink-call-assistant.html) — интерактивный
 * скрипт исходящего звонка с чек-листом и FAQ.
 *
 * Сами инструкции — автономные standalone-HTML в `public/onboarding/avito/`,
 * перекрашены под палитру Стрижа. Раздаются Caddy как статика, открываются в новой вкладке.
 */
export function AvitoHelpPage() {
  const { t } = useTranslation()
  return (
    <div>
      <PageHeader
        title={t('help.avito.title')}
        subtitle={t('help.avito.subtitle')}
      />

      <Link
        to="/help"
        className="mb-5 inline-flex items-center gap-1.5 text-sm text-[color:var(--stryzh-color-text-secondary)] hover:text-[color:var(--stryzh-color-text-primary)]"
      >
        <ArrowLeft size={15} />
        {t('help.avito.back')}
      </Link>

      <div className="flex flex-col gap-5">
        <InstructionCard
          href="/onboarding/avito/carlink-call-assistant.html"
          icon={PhoneCall}
          title={t('help.avito.callAssistant.title')}
          roles={t('help.avito.callAssistant.roles')}
          text={t('help.avito.callAssistant.text')}
        />
        <InstructionCard
          href="/onboarding/avito/with-strizh-moscow.html"
          title={t('help.avito.withStrizh.title')}
          roles={t('help.avito.withStrizh.roles')}
          text={t('help.avito.withStrizh.text')}
        />
        <InstructionCard
          href="/onboarding/avito/without-strizh.html"
          title={t('help.avito.withoutStrizh.title')}
          roles={t('help.avito.withoutStrizh.roles')}
          text={t('help.avito.withoutStrizh.text')}
        />
      </div>
    </div>
  )
}

function InstructionCard({
  href,
  title,
  roles,
  text,
  icon: Icon = ScrollText,
}: {
  href: string
  title: string
  roles: string
  text: string
  icon?: LucideIcon
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-start gap-4 rounded-lg border border-[color:var(--stryzh-color-accent-orange)]/50 bg-gradient-to-br from-[color:var(--stryzh-color-accent-orange)]/10 to-transparent p-4 transition hover:border-[color:var(--stryzh-color-accent-orange)] hover:from-[color:var(--stryzh-color-accent-orange)]/15"
    >
      <div className="mt-0.5 rounded-md bg-[color:var(--stryzh-color-accent-orange)]/20 p-2 text-[color:var(--stryzh-color-accent-orange)]">
        <Icon size={20} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
          {title}
          <ArrowUpRight size={16} className="opacity-60 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
        <div className="mt-1 text-xs font-medium uppercase tracking-wide text-[color:var(--stryzh-color-accent-orange)]">
          {roles}
        </div>
        <div className="mt-1.5 text-sm text-[color:var(--stryzh-color-text-secondary)]">{text}</div>
      </div>
    </a>
  )
}
